// « L'agent modifie l'album » + « Demandes du client » (owner, 2026-10-03).
//
// « When I receive "album edited by hand", the AI hands off. I need an on/off
// … say there are 5 tasks and I did 3: I check them; when I turn the agent on,
// it continues with the other tasks. »
//
// ONE component, two places: the order page (section 4, next to the preview)
// and the order panel beside the conversation (desktop column and the phone
// sheet behind the order chip). Worker: routes/clientRequests.ts.
//
//   the switch   album edits only — the chat assistant keeps talking. OFF
//                after a team member's hand edit, and it says so with the time.
//   the list     every request the client made (3D-preview notes + WhatsApp),
//                in the order she asked. A tick = done by you: the agent never
//                redoes it; untick = back to the agent. Done ones fold away.
//
// 44 px targets everywhere; colours only from the adm-*/status-* tokens, so
// night mode follows (index.css).
//
// Guard: ClientRequestsCard.test.tsx
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, ImageIcon, ListChecks, MessageCircle, RotateCcw } from 'lucide-react';
import { clientRequestsApi, type AgentEditSwitch, type ClientRequestItem, type ClientRequestsSnapshot } from '@/lib/clientRequestsApi';
import { useT } from '@/lib/i18n';
import { useCan } from '@/lib/actions';
import { useAuthStore } from '@/store/useAuthStore';
import { previewPageHref } from '@/lib/previewAlbumParam';
import { COVER_BACK_PAGE, COVER_FRONT_PAGE } from '@/lib/previewNoteTarget';

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

/** « 14:05 » today, « 02/10 14:05 » another day. */
export function when(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '?';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '?';
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === now.toDateString()
    ? time : `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${time}`;
}

const isMe = (by: string | null | undefined, me: string | null | undefined) =>
  Boolean(by && me && by.toLowerCase() === me.toLowerCase());

/** The one line under the switch. */
export function switchLine(s: AgentEditSwitch, me: string | null | undefined, t: Translate): string {
  if (s.on) return t('On — l’agent fait les demandes ouvertes, à partir des pages actuelles.');
  const at = when(s.since);
  if (s.reason === 'hand_edit') {
    return isMe(s.by, me)
      ? t('Off — vous avez modifié à la main à {t}', { t: at })
      : t('Off — modifié à la main par {who} à {t}', { who: s.by ?? '?', t: at });
  }
  return t('Off — coupé par {who} à {t}', { who: isMe(s.by, me) ? t('vous') : (s.by ?? '?'), t: at });
}

/** Who did it and when, or why it is not done. */
export function statusLine(i: ClientRequestItem, me: string | null | undefined, t: Translate): string {
  switch (i.status) {
    case 'open': return t('À faire');
    case 'done_owner': return isMe(i.by, me)
      ? t('Fait par vous · {t}', { t: when(i.at) })
      : t('Fait par {who} · {t}', { who: i.by ?? '?', t: when(i.at) });
    case 'done_agent': return i.at ? t('Fait par l’agent · {t}', { t: when(i.at) }) : t('Fait par l’agent');
    case 'done': return t('Fait');
    case 'agent_failed': return t('L’agent n’a pas pu : {why}', { why: i.reason ?? '?' });
    case 'needs_owner': return t('À vous : {why}', { why: i.reason ?? '?' });
  }
}

export type RequestFilter = 'todo' | 'done' | 'all';
export type RequestSort = 'asked' | 'page';

/**
 * What the list shows (owner, 2026-10-07): To do / Done / All, in the order she
 * asked or by page (album, then page; WhatsApp requests with no page last, in
 * the order she asked). Pure — guard: ClientRequestsCard.test.tsx.
 */
export function visibleRequests(items: ClientRequestItem[], filter: RequestFilter, sort: RequestSort): ClientRequestItem[] {
  const kept = items.filter((i) => (filter === 'all' ? true : filter === 'done' ? isDone(i) : !isDone(i)));
  if (sort === 'asked') return kept;
  const rank = (i: ClientRequestItem) => (i.page == null ? Number.POSITIVE_INFINITY : (i.albumIndex ?? 1) * 100_000 + i.page);
  return kept.map((i, n) => ({ i, n })).sort((a, b) => rank(a.i) - rank(b.i) || a.n - b.n).map((x) => x.i);
}

const isDone = (i: ClientRequestItem) => i.status === 'done_owner' || i.status === 'done_agent' || i.status === 'done';
const TONE: Record<ClientRequestItem['status'], string> = {
  open: 'text-adm-muted',
  done_owner: 'text-status-done',
  done_agent: 'text-status-done',
  done: 'text-status-done',
  agent_failed: 'text-status-stuck',
  needs_owner: 'text-status-waiting',
};
const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A24A] focus-visible:ring-offset-1';

export function ClientRequestsCard({ shopifyOrderId, variant = 'card', previewUrl = null }: {
  shopifyOrderId: string; variant?: 'card' | 'section';
  /** The order's 3D preview: « Aperçu · p. 50 » opens it at that page. */
  previewUrl?: string | null;
}) {
  const { t } = useT();
  const can = useCan();
  const allowed = can('order.agent_album_edits');
  const me = useAuthStore((s) => (s as { user?: { email?: string | null } | null }).user?.email ?? null);
  const qc = useQueryClient();
  const key = ['client-requests', shopifyOrderId];
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => clientRequestsApi.get(shopifyOrderId),
    enabled: allowed && Boolean(shopifyOrderId),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
  const landed = (next: ClientRequestsSnapshot) => {
    qc.setQueryData(key, next);
    // The preview card's note list reads the same notes.
    qc.invalidateQueries({ queryKey: ['preview-review', shopifyOrderId] });
  };
  const flip = useMutation({ mutationFn: (on: boolean) => clientRequestsApi.setSwitch(shopifyOrderId, on), onSuccess: landed });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<RequestFilter>('todo');
  const [sort, setSort] = useState<RequestSort>('page');
  const shown = useMemo(() => visibleRequests(data?.items ?? [], filter, sort), [data, filter, sort]);
  const check = useMutation({
    mutationFn: (v: { id: string; done: boolean }) => clientRequestsApi.check(shopifyOrderId, v.id, v.done),
    onMutate: (v) => setBusyId(v.id),
    onSettled: () => setBusyId(null),
    onSuccess: landed,
  });

  if (!allowed) return null;
  const shell = variant === 'card' ? 'rounded-xl border border-adm-line bg-adm-surface p-4' : '';
  // « Long lists get their own frame »: the section scrolls inside a fixed
  // height, its header (counts, switch, filter) stays on top.
  const frame = 'max-h-[560px] overflow-y-auto overscroll-contain';
  const headBg = variant === 'card' ? 'bg-adm-surface' : 'bg-white dark:bg-adm-surface';

  if (isLoading) {
    return (
      <div className={shell} aria-busy="true" aria-label={t('Chargement des demandes du client')}>
        <div className="h-4 w-36 rounded bg-adm-surface-3 animate-pulse" />
        <div className="h-16 w-full rounded-lg bg-adm-surface-3 animate-pulse mt-3" />
        <div className="h-11 w-full rounded-lg bg-adm-surface-3 animate-pulse mt-3" />
      </div>
    );
  }
  if (isError || !data) {
    return <div className={`${shell} text-[12.5px] text-status-stuck`} role="alert">{t('Les demandes n’ont pas pu être chargées. Réessayez dans un instant.')}</div>;
  }

  const sw = data.switch;
  const open = data.items.filter((i) => !isDone(i));
  const done = data.items.filter(isDone);
  const waWaits = data.album_agent_mode !== 'live' && open.some((i) => i.source === 'whatsapp' && i.status === 'open');
  const failed = (flip.error ?? check.error) as Error | null;

  return (
    <section className={`${shell} ${frame} ${variant === 'card' ? 'pt-0' : ''}`} aria-labelledby={`creq-${shopifyOrderId}`} data-no-ptr>
      <div className={`sticky top-0 z-10 ${headBg} ${variant === 'card' ? 'pt-4' : 'pt-1'} pb-2`}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 id={`creq-${shopifyOrderId}`} className="text-[14px] font-semibold text-adm-ink">{t('Demandes du client')}</h3>
        {data.items.length > 0 && (
          <span className="text-[12px] text-adm-muted tabular-nums">
            {t('{open} à faire · {done} faites', { open: open.length, done: done.length })}
          </span>
        )}
      </div>

      {/* ─── The switch ─────────────────────────────────────────────────── */}
      <div className={`mt-3 rounded-lg border border-adm-line bg-adm-surface-2 border-l-4 ${sw.on ? 'border-l-adm-gold' : 'border-l-status-waiting'} px-3 py-2`}>
        <div className="flex items-center gap-3">
          <Bot className={`h-5 w-5 shrink-0 ${sw.on ? 'text-adm-gold-deep' : 'text-status-waiting'}`} aria-hidden />
          <div className="min-w-0 flex-1">
            <p id={`creq-sw-${shopifyOrderId}`} className="text-[13.5px] font-medium text-adm-ink leading-tight">{t('L’agent modifie l’album')}</p>
            <p className={`text-[12px] leading-snug mt-0.5 ${sw.on ? 'text-adm-muted' : 'text-status-waiting'}`} aria-live="polite">
              {switchLine(sw, me, t)}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={sw.on}
            aria-labelledby={`creq-sw-${shopifyOrderId}`}
            aria-busy={flip.isPending}
            disabled={flip.isPending}
            onClick={() => flip.mutate(!sw.on)}
            className={`group relative inline-flex h-11 min-h-11 w-[60px] shrink-0 items-center justify-center rounded-full cursor-pointer disabled:cursor-wait ${FOCUS}`}
          >
            <span className={`relative block h-[28px] w-[50px] rounded-full border transition-colors duration-200 ${sw.on ? 'bg-adm-gold border-adm-gold' : 'bg-adm-surface-3 border-adm-line-strong'}`}>
              <span className={`absolute top-[2px] h-[22px] w-[22px] rounded-full bg-white shadow transition-[left] duration-200 motion-reduce:transition-none ${sw.on ? 'left-[24px]' : 'left-[2px]'}`} />
            </span>
            <span className="sr-only">{sw.on ? t('Activé') : t('Désactivé')}</span>
          </button>
        </div>
        <p className="text-[11.5px] text-adm-faint leading-snug mt-1.5">
          {!sw.on && open.length > 0
            ? t('Rallumez-le : l’agent reprend les {n} demandes ouvertes, en gardant vos modifications.', { n: open.filter((i) => i.status === 'open').length })
            : t('Pages de l’album seulement — l’assistant continue de répondre au client dans tous les cas.')}
        </p>
      </div>

      {data.items.length > 0 && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <div role="radiogroup" aria-label={t('Afficher')} className="inline-flex rounded-lg border border-adm-line overflow-hidden">
            {([['todo', t('À faire')], ['done', t('Faites')], ['all', t('Toutes')]] as const).map(([k, label]) => (
              <button key={k} type="button" role="radio" aria-checked={filter === k} onClick={() => setFilter(k)}
                className={`h-8 px-2.5 text-[12px] cursor-pointer ${FOCUS} ${filter === k ? 'bg-adm-gold-wash text-adm-gold-ink font-semibold' : 'bg-transparent text-adm-muted hover:bg-adm-surface-2'}`}>
                {label}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setSort(sort === 'page' ? 'asked' : 'page')}
            className={`h-8 px-2.5 rounded-lg border border-adm-line text-[12px] text-adm-muted hover:bg-adm-surface-2 cursor-pointer ${FOCUS}`}>
            {sort === 'page' ? t('Tri : page ↑') : t('Tri : ordre demandé')}
          </button>
        </div>
      )}
      </div>

      {waWaits && (
        <p className="mt-2 text-[11.5px] text-status-waiting leading-snug">
          {t('L’agent album WhatsApp est sur « {mode} » dans les réglages : les demandes WhatsApp attendent.', { mode: data.album_agent_mode })}
        </p>
      )}
      {failed && (
        <p role="alert" className="mt-2 text-[12px] text-status-stuck">{t('Non enregistré : {why}', { why: failed.message })}</p>
      )}

      {/* ─── The checklist ──────────────────────────────────────────────── */}
      {data.items.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-adm-muted">{t('Aucune demande du client pour l’instant.')}</p>
      ) : shown.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-adm-muted">
          {filter === 'todo' ? t('Tout est fait.') : t('Rien de fait pour l’instant.')}
        </p>
      ) : (
        <ul className="mt-1 divide-y divide-adm-line">
          {shown.map((i) => <Row key={i.id} i={i} me={me} busy={busyId === i.id} previewUrl={previewUrl} onCheck={(d) => check.mutate({ id: i.id, done: d })} />)}
        </ul>
      )}
    </section>
  );
}

function Row({ i, me, busy, previewUrl, onCheck }: {
  i: ClientRequestItem; me: string | null; busy: boolean; previewUrl: string | null; onCheck: (done: boolean) => void;
}) {
  const { t } = useT();
  const done = isDone(i);
  const giveBack = i.status === 'agent_failed' || i.status === 'needs_owner';
  const words = i.text || t('{n} photo(s) envoyée(s)', { n: i.photos });
  // Where a preview note is pinned: a page, or one of the covers (0 = front,
  // -1 = back — previewNoteTarget.ts, #1210). « p. 0 » would mean nothing.
  const cover = i.page === COVER_FRONT_PAGE || i.page === COVER_BACK_PAGE;
  const multi = !!i.albumIndex && i.albumIndex > 1;
  const previewPin = i.page === COVER_FRONT_PAGE
    ? (multi ? t('Album {a} · couverture', { a: i.albumIndex ?? 1 }) : t('Aperçu · couverture'))
    : i.page === COVER_BACK_PAGE
      ? (multi ? t('Album {a} · dos de l’album', { a: i.albumIndex ?? 1 }) : t('Aperçu · dos de l’album'))
      : multi ? t('Album {a} · p. {p}', { a: i.albumIndex ?? 1, p: i.page ?? '?' }) : t('Aperçu · p. {p}', { p: i.page ?? '?' });
  return (
    <li data-request-row={i.id} className="flex items-start gap-1 py-1" aria-busy={busy}>
      <label className={`min-h-11 min-w-11 -ml-2 flex items-center justify-center shrink-0 rounded-lg cursor-pointer has-[:disabled]:cursor-wait has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#C9A24A]`}>
        <input
          type="checkbox"
          checked={done}
          disabled={busy}
          onChange={(e) => onCheck(e.target.checked)}
          aria-label={done ? t('Rouvrir pour l’agent : {text}', { text: words }) : t('Marquer comme fait : {text}', { text: words })}
          className="h-5 w-5 accent-[#C9A24A] cursor-pointer focus:outline-none"
        />
      </label>
      <div className="min-w-0 flex-1 py-2">
        <p className={`text-[13.5px] leading-snug break-words ${done ? 'text-adm-muted line-through decoration-adm-line-strong' : 'text-adm-ink'}`}>{words}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px]">
          {i.source === 'preview' && previewUrl && (cover || (i.page ?? 0) >= 1) ? (
            // « Click "Preview · p. 50" opens the 3D preview at that page. »
            // A cover note (#1210) opens it on the cover — no page to jump to.
            <a href={previewPageHref(previewUrl, cover ? null : i.page, i.albumIndex)} target="_blank" rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 rounded-md bg-adm-gold-wash px-1.5 py-0.5 text-adm-gold-ink underline-offset-2 hover:underline ${FOCUS}`}>
              <ImageIcon className="h-3 w-3" aria-hidden />
              {previewPin}
            </a>
          ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-adm-surface-3 px-1.5 py-0.5 text-adm-ink-2">
            {i.source === 'preview'
              ? <><ImageIcon className="h-3 w-3" aria-hidden />{previewPin}</>
              : <><MessageCircle className="h-3 w-3" aria-hidden />WhatsApp{i.photos > 0 && i.text ? ` · ${t('+{n} photo(s)', { n: i.photos })}` : ''}</>}
          </span>
          )}
          {(i.photosLost ?? 0) > 0 && (
            <span className="font-semibold text-red-700 dark:text-red-400">{t('{n} jamais reçue(s) — à renvoyer en Document', { n: i.photosLost ?? 0 })}</span>
          )}
          {i.sourceIds.length > 1 && <span className="text-adm-faint">{t('dit {n} fois', { n: i.sourceIds.length })}</span>}
          <span className={TONE[i.status]}>{statusLine(i, me, t)}</span>
        </div>
      </div>
      {giveBack && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onCheck(false)}
          className={`mt-0.5 shrink-0 inline-flex items-center gap-1 min-h-11 px-2.5 rounded-lg border border-adm-line bg-adm-surface text-[12px] font-medium text-adm-ink-2 hover:bg-adm-surface-2 cursor-pointer disabled:cursor-wait ${FOCUS}`}
          title={t('L’agent réessaie cette demande à partir des pages actuelles.')}
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />{t('Rendre à l’agent')}
        </button>
      )}
    </li>
  );
}

/**
 * The sticky bar's way to the list (order page): « Demandes 3 », only while
 * something is left to do. ⚠️ NO NEW ROW, NO TALLER BAR: it sits beside the
 * pipeline rail, and its 44 px target is pulled back by negative margins, so
 * the bar keeps its measured height (--adm-sticky-h). Same query as the card.
 */
export function RequestsJump({ shopifyOrderId }: { shopifyOrderId: string }) {
  const { t } = useT();
  const allowed = useCan()('order.agent_album_edits');
  const { data } = useQuery({
    queryKey: ['client-requests', shopifyOrderId],
    queryFn: () => clientRequestsApi.get(shopifyOrderId),
    enabled: allowed && Boolean(shopifyOrderId),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
  if (!allowed || !data) return null;
  const left = data.items.filter((i) => !isDone(i)).length;
  if (!left) return null;
  return (
    <button
      type="button"
      onClick={() => document.getElementById('sec-requests')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      aria-label={t('Demandes du client — {n} à faire', { n: left })}
      title={t('Demandes du client — {n} à faire', { n: left })}
      className={`shrink-0 inline-flex items-center gap-1.5 min-h-11 -my-2.5 px-2 rounded-lg text-[11.5px] font-semibold text-adm-gold-ink hover:bg-adm-gold-wash cursor-pointer ${FOCUS}`}
    >
      <ListChecks className="h-4 w-4" aria-hidden />
      <span>{t('Demandes')}</span>
      <span className="min-w-5 h-5 px-1 rounded-full bg-adm-gold text-white text-[11px] leading-5 text-center tabular-nums">{left}</span>
    </button>
  );
}
